import { nativeQueueDb, convertToQueueItem, type NativeQueueEntry } from './nativeQueueDb'
import type {
  LicenceContactT,
  Metadata,
  MetadataSummary,
  QueueItemT,
} from '@/lib/types/bgSyncTypes.ts'
import type { AxiosRequestConfig } from 'axios'
import {
  MAX_RETRIES,
  MAX_RETENTION_MS,
  MAX_QUEUE_SIZE,
  COMMENT_PREVIEW_LENGTH,
  BACKOFF_BASE_MS,
  BACKOFF_MAX_MS,
  LICENCE_RETRY_INTERVAL_MS,
  QUEUE_ENQUEUED_AT_HEADER,
  LICENCE_BLOCK_HEADER,
} from './syncConstants'
import { getApiBaseUrl } from '@/config/apiConfig'
import { apiFetch, type ApiFetchInit } from '@/lib/apiFetch'
import { safeRandomUUID } from '@/lib/helpers/safeRandomUUID.ts'
import { adoptSignedMediaUrls } from '@/lib/media/adoptSignedMediaUrls'
import { markupLaneOf } from '@/lib/annotation/queuedMarkupSave'

// Dev-only debug logging
const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[NativeQueue]', ...args)

type QueueUpdateListener = () => void
type FailureListener = (meta: Metadata) => void
// `responseBody` is the parsed JSON the server returned for the queued
// request. Plumbed through specifically so callers like the bulk-edit
// mutation can read per-id outcomes from `/issues/bulk` responses that
// HTTP-wise look 200 but semantically encode partial failures inside
// `results: [...]`. Undefined for media uploads, non-JSON responses, or
// when parsing fails.
type SuccessListener = (meta: Metadata, responseBody?: unknown) => void

// Outcome of replaying one queue entry, consumed by the fastQueue
// head-of-line gate. 'transient' (will retry) and 'deferred' (in backoff)
// block later same-requestUUID rows for the pass; 'synced' and 'permanent'
// let the lane continue. 'licenceBlocked' holds the whole group: a PATCH that
// depends on a refused POST must not go ahead and turn into a separate 404.
type ProcessOutcome = 'synced' | 'transient' | 'permanent' | 'deferred' | 'licenceBlocked'

/** A stored JSON body as an object; anything unparseable (a multipart upload's null) is null. */
function parseBody(body: string | null): unknown {
  if (!body) return null
  try {
    return JSON.parse(body)
  } catch {
    return null
  }
}

/**
 * ISO 8601 with an explicit `+00:00` offset and milliseconds, the form the server parses first
 * (`RFC3339_EXTENDED` in LicenceWriteGuard::enqueuedAt). `toISOString()` ends in `Z`, which that
 * format does not promise to accept, and an unparsed header silently costs the grace window.
 */
export function formatEnqueuedAt(ms: number): string {
  return new Date(ms).toISOString().replace(/Z$/, '+00:00')
}

/**
 * A permanent failure recorded as HTTP 403 — by `status` on entries new enough to carry it,
 * by the `HTTP 403:` message on older ones.
 */
function isPermanent403(meta: Metadata): boolean {
  if (!meta.permanentFailure) return false
  if (meta.status === 403) return true
  return typeof meta.error === 'string' && meta.error.startsWith('HTTP 403')
}

function readLicenceContact(body: Record<string, unknown> | null): LicenceContactT | null {
  const contact = body?.['contact']
  if (!contact || typeof contact !== 'object') return null
  const { email, phone } = contact as Record<string, unknown>
  return {
    email: typeof email === 'string' ? email : null,
    phone: typeof phone === 'string' ? phone : null,
  }
}

class NativeQueueManager {
  private listeners: Set<QueueUpdateListener> = new Set()
  private failureListeners: Set<FailureListener> = new Set()
  private successListeners: Set<SuccessListener> = new Set()
  private fastSyncRunning = false
  private mediaSyncRunning = false
  private syncPromise: Promise<void> | null = null
  private maintenancePaused = false
  private authorProvider: () => number | null = () => null
  /** Groups to replay despite `licenceBlocked` on the next pass (manual retry of one group). */
  private licenceRetryGroups = new Set<string>()
  /** Replay every licence-held group on the next pass (renewal, "sync now"). */
  private licenceRetryAll = false
  /**
   * Per held group: when it was last replayed or refused. Per group, not global, so a group
   * refused a minute ago cannot keep postponing the hourly replay of a group refused a day ago.
   * A group with no entry is due at once, so the first pass after start replays every held
   * group: that is the cheapest moment to notice a licence renewed while the app was closed.
   */
  private licenceRetryAtByGroup = new Map<string, number>()
  /** Groups refused by the licence during the current pass; the media pass must skip them. */
  private licenceBlockedThisPass = new Set<string>()
  // Groups whose hold was lifted during the current pass. The pass read its rows before the
  // lift, so their in-memory metadata still says "held"; see processEntry.
  private licenceLiftedThisPass = new Set<string>()
  /** Once per process: backfill `enqueuedAt` and re-arm pre-C8 403s (see prepareEntriesOnce). */
  private startupPrepared = false

  /**
   * Who is signed in when a request is queued. The queue is per device and survives a logout, so
   * a consumer asking "is THIS user's write still pending" (the guide hints) needs the author on
   * the entry. Registered by the app shell; the user store cannot be imported here without a
   * cycle (`store/user.ts` imports `api-client.ts`, which imports this module).
   */
  setAuthorProvider(provider: () => number | null): void {
    this.authorProvider = provider
  }

  /**
   * Test seam: the manager is a module singleton, so the per-process licence state (the
   * startup preparation, the hourly clock, pending retry requests) would otherwise leak between
   * tests and make "first pass after start" unobservable after the first test.
   */
  resetRuntimeStateForTests(): void {
    this.licenceRetryGroups.clear()
    this.licenceRetryAll = false
    this.licenceRetryAtByGroup.clear()
    this.licenceBlockedThisPass = new Set()
    this.licenceLiftedThisPass = new Set()
    this.startupPrepared = false
  }

  addListener(listener: QueueUpdateListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  addFailureListener(listener: FailureListener): () => void {
    this.failureListeners.add(listener)
    return () => this.failureListeners.delete(listener)
  }

  addSuccessListener(listener: SuccessListener): () => void {
    this.successListeners.add(listener)
    return () => this.successListeners.delete(listener)
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener()
    })
  }

  private notifyFailure(meta: Metadata): void {
    this.failureListeners.forEach(listener => {
      listener(meta)
    })
  }

  private notifySuccess(meta: Metadata, responseBody?: unknown): void {
    this.successListeners.forEach(listener => {
      listener(meta, responseBody)
    })
  }

  async enqueueRequest(
    config: AxiosRequestConfig,
    formData?: FormData,
  ): Promise<{ status: string; metadata: Metadata }> {
    // Check queue size to prevent unbounded growth
    const currentSize = await nativeQueueDb.requests.count()
    if (currentSize >= MAX_QUEUE_SIZE) {
      throw new Error(
        `Queue is full (${MAX_QUEUE_SIZE} items). Please sync pending items or check your connection.`,
      )
    }

    const url = config.url ?? ''
    const method = (config.method?.toUpperCase() ?? 'POST') as 'POST' | 'PATCH'
    const headers = (config.headers as Record<string, string>) ?? {}

    // Use API base URL for iOS native (capacitor://localhost won't work for fetch)
    const apiBaseUrl = getApiBaseUrl() || window.location.origin
    const path = new URL(url, apiBaseUrl).pathname
    const type = path.split('/').filter(Boolean).shift() ?? ''
    const isMedia = type === 'media_objects'

    let queueName: 'fastQueue' | 'mediaQueue'
    if (path.includes('/media_objects')) {
      queueName = 'mediaQueue'
    } else if (
      path.includes('/issues') ||
      path.includes('/comments') ||
      path.includes('/locations')
    ) {
      queueName = 'fastQueue'
    } else {
      throw new Error(`Unknown endpoint for queuing: ${path}`)
    }

    let data: Record<string, unknown> = {}
    let fileBlob: Blob | null = null
    let fileName: string | null = null

    if (isMedia && formData) {
      // Handle FormData properly - preserve duplicate keys as arrays
      for (const [key, value] of formData.entries()) {
        if (key === 'file') continue // Handle file separately
        if (data[key] !== undefined) {
          // Convert to array if duplicate key
          if (!Array.isArray(data[key])) {
            data[key] = [data[key]]
          }
          ;(data[key] as unknown[]).push(value)
        } else {
          data[key] = value
        }
      }
      // anno-trace: snapshot what the queue captured from FormData.
      // Inlined via JSON.stringify because Capacitor Android's logcat
      // bridge only relays the FIRST arg of console.*.
      console.info(
        '[anno-trace] nativeQueue captured formData ' +
          JSON.stringify({
            keys: Object.keys(data),
            annotationDataType: typeof data['annotationData'],
            annotationDataLength:
              typeof data['annotationData'] === 'string'
                ? (data['annotationData'] as string).length
                : null,
          }),
      )
      const file = formData.get('file')
      if (file instanceof Blob) {
        fileBlob = file
        fileName = file instanceof File ? file.name : 'file'
        debug(`File extracted from FormData: ${fileName}, size: ${file.size}, type: ${file.type}`)
      } else {
        debug(`WARNING: No file found in FormData, file value: ${typeof file}`)
      }
    } else if (config.data) {
      if (typeof config.data === 'string') {
        try {
          data = JSON.parse(config.data)
        } catch (error) {
          debug('Failed to parse request data:', error)
          throw new Error('Invalid request data format')
        }
      } else {
        data = config.data ?? {}
      }
    }

    const changedFields = Object.keys(data)
    // Serialized body for the PATCH dedup check below. Identical body = true
    // double-tap (dropped); a different value for the same field set is a
    // genuine new edit that must queue its own row so the latest value wins
    // on FIFO replay.
    const newBodyJson = JSON.stringify(data)

    const summary: MetadataSummary = {}
    if (changedFields.includes('labels')) summary.labels = data.labels as string[]
    if (changedFields.includes('status')) summary.status = data.status as string
    if (changedFields.includes('recipient')) summary.recipient = data.recipient as string
    if (changedFields.includes('location')) summary.location = data.location as string
    if (changedFields.includes('issue')) summary.issue = data.issue as string

    let name = isMedia ? fileName : (data.name as string | undefined)
    if (data.text) {
      const text = data.text as string
      name =
        text.substring(0, COMMENT_PREVIEW_LENGTH) +
        (text.length > COMMENT_PREVIEW_LENGTH ? '...' : '')
    }

    const metadata: Metadata = {
      method,
      type,
      changedFields,
      summary,
      // Native queue stores blobs separately, so we don't include file here
      // The name field contains the filename for media uploads
      name: name ?? '',
      mediaObjectUUID: isMedia ? ((data.mediaObjectUUID as string | null) ?? null) : null,
      requestUUID: (data.requestUUID as string) || safeRandomUUID(),
      fileUUID: isMedia ? ((data.fileUUID as string | null) ?? null) : null,
      fileCacheKey: null,
      authorId: this.authorProvider(),
      enqueuedAt: Date.now(),
      failed: false,
      retries: 0,
      permanentFailure: false,
      error: '',
    }

    // Wrap dedup check + blob storage + request insert in a single Dexie transaction
    // to prevent TOCTOU race where two identical requests both pass the dedup check
    // before either is inserted (e.g., double-tap on mobile).
    let result: { status: string; metadata: Metadata } | null = null
    let queued = false

    // Convert blob to ArrayBuffer outside the transaction (async blob reads
    // can cause the IndexedDB transaction to auto-commit on iOS WebKit)
    let preparedBlob: Blob | null = null
    let blobCacheKey: string | null = null
    if (fileBlob) {
      blobCacheKey = `/native-file-cache/${metadata.fileUUID ?? safeRandomUUID()}`
      const arrayBuffer = await fileBlob.arrayBuffer()
      preparedBlob = new Blob([arrayBuffer], { type: fileBlob.type })
      debug(
        `Blob prepared: ${blobCacheKey}, original size: ${fileBlob.size}, converted size: ${preparedBlob.size}`,
      )
    } else if (isMedia) {
      debug(`WARNING: Media upload queued without file blob`)
    }

    try {
      await nativeQueueDb.transaction(
        'rw',
        nativeQueueDb.requests,
        nativeQueueDb.blobs,
        async () => {
          const existingEntries = await nativeQueueDb.requests
            .filter(e => {
              const m = e.metadata
              const basicMatch =
                m.method === metadata.method &&
                m.type === metadata.type &&
                m.requestUUID === metadata.requestUUID

              // For media uploads, also require fileUUID match (same file)
              if (metadata.fileUUID && m.fileUUID) {
                return basicMatch && m.fileUUID === metadata.fileUUID
              }

              // For PATCH (edits) the issue's requestUUID links all edits to
              // that issue, so dedup also requires the same changedFields AND
              // an identical serialized body. Identical body = true double-tap
              // (dropped). A DIFFERENT value for the same field set is a genuine
              // new edit that queues its own row, so the latest value wins when
              // fastQueue replays in FIFO order (latest-wins via ordered replay
              // — no coalescing/revision guard; see lib/AGENTS.md +
              // audit/domains/offline.md).
              if (metadata.method === 'PATCH' && m.changedFields && metadata.changedFields) {
                const sameFields =
                  m.changedFields.length === metadata.changedFields.length &&
                  m.changedFields.every(f => metadata.changedFields!.includes(f))
                return basicMatch && sameFields && e.body === newBodyJson
              }

              return basicMatch
            })
            .toArray()

          if (existingEntries.length > 0) {
            debug(`Duplicate entry ignored (${metadata.requestUUID})`)
            result = { status: 'duplicate', metadata }
            return // Abort rest of transaction body (Dexie still commits)
          }

          if (preparedBlob && blobCacheKey) {
            await nativeQueueDb.blobs.put({ id: blobCacheKey, blob: preparedBlob })

            // Verify blob was stored correctly
            const storedBlob = await nativeQueueDb.blobs.get(blobCacheKey)
            if (!storedBlob?.blob || storedBlob.blob.size === 0) {
              throw new Error(
                `Failed to store blob: verification failed, size: ${storedBlob?.blob?.size ?? 0}`,
              )
            }
            debug(`Blob stored and verified: ${blobCacheKey}, size: ${storedBlob.blob.size}`)

            metadata.fileCacheKey = blobCacheKey
          }

          const bodyToStore = isMedia
            ? JSON.stringify({ ...data, file: undefined, fileCacheKey: metadata.fileCacheKey })
            : JSON.stringify(data)

          const entry: NativeQueueEntry = {
            queueName,
            url: new URL(url, apiBaseUrl).href,
            method,
            headers: { ...headers },
            body: bodyToStore,
            timestamp: Date.now(),
            metadata,
          }

          await nativeQueueDb.requests.add(entry)

          debug(
            `Request queued to ${queueName}: ${metadata.requestUUID}, fileUUID: ${metadata.fileUUID}, cacheKey: ${metadata.fileCacheKey}`,
          )

          result = { status: 'queued', metadata }
          queued = true
        },
      )

      if (queued) {
        this.notifyListeners()
      }

      return result!
    } catch (error) {
      console.error('[NativeQueue] Failed to queue request:', error)
      throw new Error(
        `Failed to queue request: ${error instanceof Error ? error.message : 'IndexedDB error'}`,
      )
    }
  }

  async getAllEntries(): Promise<QueueItemT[]> {
    const entries = await nativeQueueDb.requests.toArray()
    return entries.map(e => convertToQueueItem(e))
  }

  async deleteEntry(id: number): Promise<void> {
    const entry = await nativeQueueDb.requests.get(id)
    if (entry?.metadata.fileCacheKey) {
      await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
    }
    await nativeQueueDb.requests.delete(id)
    debug(`Deleted entry ${id}`)
    this.notifyListeners()
  }

  async deleteAllFailed(): Promise<number[]> {
    // A licence-held group is the user's unsent work waiting for a renewal, not a failure:
    // "remove all failed" must not delete it, even when one of its rows also failed for an
    // unrelated reason. Removing it is the explicit, per-group `deleteLicenceBlockedGroup`.
    const heldGroups = await this.getLicenceBlockedGroups()
    const entries = await nativeQueueDb.requests
      .filter(
        e =>
          (Boolean(e.metadata.failed) || Boolean(e.metadata.permanentFailure)) &&
          !heldGroups.has(e.metadata.requestUUID),
      )
      .toArray()

    const deletedIds: number[] = []
    for (const entry of entries) {
      if (entry.id !== undefined) {
        if (entry.metadata.fileCacheKey) {
          await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
        }
        await nativeQueueDb.requests.delete(entry.id)
        deletedIds.push(entry.id)
      }
    }

    debug(`Deleted ${deletedIds.length} failed entries`)
    this.notifyListeners()

    return deletedIds
  }

  /**
   * Metadata reset applied when a failed entry is re-queued for retry. Besides
   * the failure flags it MUST clear:
   *   - `nextRetryAt` — doPerformSync skips entries whose backoff hasn't elapsed
   *     (`if (nextRetryAt && Date.now() < nextRetryAt) return`), so a retry that
   *     left it set would be silently backoff-blocked until the window passed.
   *   - `status` / `errorDetail` — the structured failure fields the failure
   *     listeners surface as UX; leaving them stale would mislabel the retry.
   * Single source of truth so retryEntry + retryAllFailed stay identical.
   *
   * It deliberately leaves `enqueuedAt` and `licenceBlocked` alone: the first is the grace
   * window's anchor, the second is what keeps a held group out of stale cleanup until it has
   * actually been delivered.
   */
  private clearFailureState(meta: Metadata): Metadata {
    return {
      ...meta,
      failed: false,
      permanentFailure: false,
      retries: 0,
      error: '',
      status: undefined,
      errorDetail: undefined,
      nextRetryAt: undefined,
    }
  }

  async retryEntry(id: number): Promise<void> {
    const entry = await nativeQueueDb.requests.get(id)
    if (!entry) return

    if (entry.metadata.licenceBlocked) {
      await this.retryLicenceBlockedGroup(entry.metadata.requestUUID)
      return
    }

    await nativeQueueDb.requests.update(id, {
      metadata: this.clearFailureState(entry.metadata),
      timestamp: Date.now(),
    })

    this.notifyListeners()
    await this.performSync()
  }

  /**
   * Reset ALL failed / permanently-failed entries and re-sync. Backs the manual
   * "sync now" button so a user can replay everything that's stuck — including
   * permanent failures — in one action. Mirrors `deleteAllFailed`'s filter.
   */
  async retryAllFailed(): Promise<void> {
    const entries = await nativeQueueDb.requests
      .filter(e => Boolean(e.metadata.failed) || Boolean(e.metadata.permanentFailure))
      .toArray()

    let changed = 0
    for (const entry of entries) {
      if (entry.id === undefined) continue
      await nativeQueueDb.requests.update(entry.id, {
        metadata: this.clearFailureState(entry.metadata),
        timestamp: Date.now(),
      })
      changed++
    }

    // "Sync now" also replays licence-held groups: a replay costs no retry budget, and a user
    // pressing it after paying expects the held work to go.
    this.licenceRetryAll = true

    if (changed > 0) this.notifyListeners()
    await this.performSync()
    // As in retryLicenceBlocked: a pass already in flight chose its rows before this request.
    if (this.licenceRetryAll) await this.performSync()
  }

  /**
   * Replay one licence-held group: reset the failure state of every row in it and replay it in
   * FIFO order on the next pass, media last. `licenceBlocked` stays set until each row is
   * delivered, so the group stays out of stale cleanup while it is still being retried.
   */
  async retryLicenceBlockedGroup(requestUUID: string): Promise<void> {
    const rows = await nativeQueueDb.requests
      .filter(e => e.metadata.requestUUID === requestUUID)
      .toArray()
    for (const row of rows) {
      if (row.id === undefined) continue
      await nativeQueueDb.requests.update(row.id, {
        metadata: this.clearFailureState(row.metadata),
        timestamp: Date.now(),
      })
    }
    this.licenceRetryGroups.add(requestUUID)
    if (rows.length > 0) this.notifyListeners()
    await this.performSync()
    // performSync() joins a pass already in flight, which chose its rows before this request;
    // run one more so the group is actually replayed before the caller's spinner stops.
    if (this.licenceRetryGroups.has(requestUUID)) await this.performSync()
  }

  /**
   * Replay every licence-held group. Called when a stage read shows the project owner's licence
   * became valid again. Over-triggering is harmless: a group whose licence is still expired
   * answers 403 again without consuming a retry.
   */
  async retryLicenceBlocked(): Promise<void> {
    this.licenceRetryAll = true
    await this.performSync()
    if (this.licenceRetryAll) await this.performSync()
  }

  /**
   * The user's confirmed removal of a licence-held group: every row sharing the requestUUID,
   * fast and media, with their blobs. Removing only the head would leave dependent PATCHes and
   * media to replay later against an issue that never existed.
   */
  async deleteLicenceBlockedGroup(requestUUID: string): Promise<number[]> {
    const rows = await nativeQueueDb.requests
      .filter(e => e.metadata.requestUUID === requestUUID)
      .toArray()
    const deletedIds: number[] = []
    for (const row of rows) {
      if (row.id === undefined) continue
      if (row.metadata.fileCacheKey) {
        await nativeQueueDb.blobs.delete(row.metadata.fileCacheKey)
      }
      await nativeQueueDb.requests.delete(row.id)
      deletedIds.push(row.id)
    }
    this.licenceRetryGroups.delete(requestUUID)
    this.licenceRetryAtByGroup.delete(requestUUID)
    if (deletedIds.length > 0) this.notifyListeners()
    return deletedIds
  }

  private async getLicenceBlockedGroups(): Promise<Set<string>> {
    const held = await nativeQueueDb.requests
      .filter(e => Boolean(e.metadata.licenceBlocked))
      .toArray()
    return new Set(held.map(e => e.metadata.requestUUID))
  }

  async performSync(): Promise<void> {
    // Mutex: if sync is already running, wait for it instead of starting another
    if (this.syncPromise) {
      debug('Sync already in progress, waiting...')
      return this.syncPromise
    }

    this.syncPromise = this.doPerformSync()
    try {
      await this.syncPromise
    } finally {
      this.syncPromise = null
    }
  }

  /**
   * Pause/resume queue draining for a maintenance window. While paused,
   * doPerformSync() early-returns so locked-out (non-team) users don't burn the
   * 3-retry budget against maintenance 503s. Resuming triggers an immediate
   * drain. Driven by useMaintenanceStatus from GlobalLayout.
   */
  setMaintenancePaused(paused: boolean): void {
    if (this.maintenancePaused === paused) return
    this.maintenancePaused = paused
    if (!paused) {
      void this.performSync()
    }
  }

  private async doPerformSync(): Promise<void> {
    debug('performSync started')

    if (this.maintenancePaused) {
      debug('Maintenance mode active — skipping queue sync')
      return
    }

    // Before cleanup: a pre-C8 403 re-armed here must already be protected when cleanup runs,
    // or a month-old one would be deleted before it got its one recheck.
    await this.prepareEntriesOnce()

    // Clean up stale entries (older than 30 days) before syncing
    await this.cleanupStaleEntries()

    // Licence-held groups: propagate the hold to every row of the group (a PATCH or photo
    // queued after the refusal must wait behind it too), then decide whether this pass replays
    // them — on request, or once per LICENCE_RETRY_INTERVAL_MS.
    const heldGroups = await this.propagateLicenceBlock()
    const now = Date.now()
    const replayAllHeld = this.licenceRetryAll
    const requestedGroups = new Set(this.licenceRetryGroups)
    this.licenceRetryAll = false
    this.licenceRetryGroups.clear()
    this.licenceBlockedThisPass = new Set()
    this.licenceLiftedThisPass = new Set()
    const replayedHeld = new Set<string>()
    for (const uuid of heldGroups) {
      const hourlyDue =
        now - (this.licenceRetryAtByGroup.get(uuid) ?? 0) >= LICENCE_RETRY_INTERVAL_MS
      if (replayAllHeld || hourlyDue || requestedGroups.has(uuid)) {
        replayedHeld.add(uuid)
        this.licenceRetryAtByGroup.set(uuid, now)
      }
    }
    const isHeld = (uuid: string) => heldGroups.has(uuid) && !replayedHeld.has(uuid)

    const fastEntries = (
      await nativeQueueDb.requests.where('queueName').equals('fastQueue').toArray()
    ).filter(e => !isHeld(e.metadata.requestUUID))

    // Held rows stay in the list so a held markup save still holds back the later saves of its
    // image (processMediaQueue); they are not replayed.
    const mediaEntries = await nativeQueueDb.requests
      .where('queueName')
      .equals('mediaQueue')
      .toArray()

    // Drain the fast queue (issue/comment creates) BEFORE the media queue.
    // A queued offline "create issue + photo" enqueues `POST /issues` (fast)
    // and `POST /media_objects` (media) sharing one requestUUID. The backend
    // binds them by requestUUID in BOTH directions (the media POST attaches to
    // an existing issue; the issue POST reverse-attaches pre-existing media —
    // AttachmentsBindStateProcessor), so SEQUENTIAL replay in either order links
    // correctly. The hazard is CONCURRENCY: draining both queues at once put
    // both POSTs in flight together, and if each request's bind-lookup runs
    // before the other has committed, neither sees the other → the photo
    // persists ORPHANED (permanent — both got 2xx, no retry) and the issue
    // reopens with no attachment. Serialising fast→media closes that window.
    await this.processFastQueue(fastEntries.filter(e => !e.metadata.permanentFailure))

    // Hold back any media upload whose same-requestUUID fast create is still
    // pending (failed / backed-off this pass): uploading it now would commit a
    // media_object before its issue/comment exists. It stays queued and replays
    // on a later pass, after the create finally lands.
    const pendingFastCreates = await this.getPendingFastCreateRequestUUIDs()
    // `mediaEntries` was read before the fast pass, so a group the licence refused during it
    // still looks replayable here; `licenceBlockedThisPass` is what holds its photos back.
    await this.processMediaQueue(
      mediaEntries.filter(e => !e.metadata.permanentFailure),
      e =>
        !isHeld(e.metadata.requestUUID) &&
        !pendingFastCreates.has(e.metadata.requestUUID) &&
        !this.licenceBlockedThisPass.has(e.metadata.requestUUID),
    )
  }

  /**
   * One-time work on the first pass after the app starts.
   *
   * 1. Backfill `enqueuedAt` from `timestamp` on rows queued before the field existed.
   * 2. Re-arm permanent 403s recorded by a pre-C8 client. That client saw a licence refusal as
   *    an ordinary 403 and kept no reason, so after the update nothing would recognise it and
   *    cleanup would delete the work 30 days after the last attempt. Each such row gets exactly
   *    one more attempt (`licenceRecheck`): a licence refusal now lands as a held group, a real
   *    permission refusal goes back to permanent and is never re-armed again.
   */
  private async prepareEntriesOnce(): Promise<void> {
    if (this.startupPrepared) return

    // One transaction, and the flag is set only after it commits: a failed write must leave
    // the preparation to the next pass, or a stale pre-C8 403 that missed its re-arm would be
    // deleted by the cleanup that runs right after this.
    let changed = false
    await nativeQueueDb.transaction('rw', nativeQueueDb.requests, async () => {
      changed = false
      const rows = await nativeQueueDb.requests.toArray()
      for (const row of rows) {
        if (row.id === undefined) continue
        let meta = row.metadata
        let touched = false
        if (typeof meta.enqueuedAt !== 'number') {
          meta = { ...meta, enqueuedAt: row.timestamp }
          touched = true
        }
        if (!meta.licenceBlocked && meta.licenceRecheck === undefined && isPermanent403(meta)) {
          meta = { ...this.clearFailureState(meta), licenceRecheck: 'pending' }
          touched = true
        }
        if (touched) {
          await nativeQueueDb.requests.update(row.id, { metadata: meta })
          changed = true
        }
      }
    })
    this.startupPrepared = true
    if (changed) this.notifyListeners()
  }

  /**
   * Marks every row of a held group as held (with the group's reason and contact) and returns
   * the held groups. Rows queued into a group after its refusal pick the hold up here.
   */
  private async propagateLicenceBlock(): Promise<Set<string>> {
    const rows = await nativeQueueDb.requests.toArray()
    const reasonByGroup = new Map<string, Metadata>()
    for (const row of rows) {
      if (row.metadata.licenceBlocked && !reasonByGroup.has(row.metadata.requestUUID)) {
        reasonByGroup.set(row.metadata.requestUUID, row.metadata)
      }
    }
    for (const row of rows) {
      const source = reasonByGroup.get(row.metadata.requestUUID)
      if (
        !source ||
        row.metadata.licenceBlocked ||
        row.metadata.permanentFailure ||
        row.id === undefined
      ) {
        continue
      }
      await nativeQueueDb.requests.update(row.id, {
        metadata: {
          ...row.metadata,
          licenceBlocked: source.licenceBlocked,
          licenceContact: source.licenceContact ?? null,
        },
      })
    }
    return new Set(reasonByGroup.keys())
  }

  /**
   * The server gave `requestUUID` an answer that is not a licence refusal (2xx or a non-licence
   * 4xx), so the licence no longer stands in its way: release the whole group back to ordinary
   * queue semantics. Its rows then replay, fail and age out like any other row.
   */
  private async liftLicenceHold(requestUUID: string): Promise<void> {
    this.licenceLiftedThisPass.add(requestUUID)
    this.licenceRetryAtByGroup.delete(requestUUID)
    const rows = await nativeQueueDb.requests
      .filter(e => e.metadata.requestUUID === requestUUID && Boolean(e.metadata.licenceBlocked))
      .toArray()
    for (const row of rows) {
      if (row.id === undefined) continue
      await nativeQueueDb.requests.update(row.id, {
        metadata: { ...row.metadata, licenceBlocked: null, licenceContact: null },
      })
    }
  }

  /**
   * The licence refused `entry`: hold its whole requestUUID group. Not a failure — no retry is
   * consumed, `failed`/`permanentFailure` stay as they were, and nothing is reported to the
   * failure listeners (whose toasts would say "no permission"). The queue screen shows held
   * groups in their own section.
   */
  private async markLicenceBlocked(
    entry: NativeQueueEntry,
    meta: Metadata,
    reason: string,
    response: Response,
  ): Promise<void> {
    let body: Record<string, unknown> | null = null
    try {
      body = (await response.clone().json()) as Record<string, unknown> | null
    } catch {
      // unreadable body — the reason from the header is enough to hold the group
    }
    const contact = readLicenceContact(body)
    const detail = typeof body?.['detail'] === 'string' ? (body['detail'] as string) : undefined
    const uuid = meta.requestUUID

    await nativeQueueDb.transaction('rw', nativeQueueDb.requests, async () => {
      const rows = await nativeQueueDb.requests
        .filter(e => e.metadata.requestUUID === uuid)
        .toArray()
      for (const row of rows) {
        if (row.id === undefined) continue
        // A row that already failed for good is not replayed, so holding it would promise a
        // delivery that never comes; it stays an ordinary failure.
        if (row.id !== entry.id && row.metadata.permanentFailure) continue
        const base = row.id === entry.id ? meta : row.metadata
        await nativeQueueDb.requests.update(row.id, {
          metadata: {
            ...base,
            licenceBlocked: reason,
            licenceContact: contact,
            ...(row.id === entry.id
              ? {
                  status: 403,
                  errorDetail: detail,
                  nextRetryAt: undefined,
                  licenceRecheck: base.licenceRecheck === 'pending' ? 'done' : base.licenceRecheck,
                }
              : {}),
          },
        })
      }
    })
    this.licenceBlockedThisPass.add(uuid)
    // A fresh refusal is the latest word on this group's licence: its next automatic replay waits
    // a full interval from here, not from a clock that may still be unset in this process.
    this.licenceRetryAtByGroup.set(uuid, Date.now())
    debug(`Licence refused ${uuid} (${reason}) — holding its group`)
    this.notifyListeners()
  }

  /**
   * requestUUIDs of fast-queue CREATE (POST) rows still present after a fast
   * drain pass — i.e. creates that did not succeed (a 2xx deletes the row).
   * Their same-requestUUID media uploads must wait so they can't be persisted
   * before the parent issue/comment exists. PATCH edits are excluded: their
   * media (if any) links via owningIssue, not a pending create's requestUUID.
   */
  private async getPendingFastCreateRequestUUIDs(): Promise<Set<string>> {
    const entries = await nativeQueueDb.requests.where('queueName').equals('fastQueue').toArray()
    return new Set(
      entries
        .filter(entry => entry.metadata.method === 'POST')
        .map(entry => entry.metadata.requestUUID),
    )
  }

  /**
   * Remove entries older than MAX_RETENTION_MS (30 days) to prevent unbounded growth.
   *
   * Two kinds of row are exempt, because deleting them deletes work the server never refused
   * for good:
   * - every row of a licence-held group — including media and edits that never got an answer
   *   of their own, since they wait behind the refused head;
   * - every row of a group whose pre-C8 403 is re-armed for its one recheck
   *   (`licenceRecheck: 'pending'`) — the media and edits behind that head never got an answer
   *   either, and if the recheck turns into a licence hold they belong to it.
   *
   * Only a row that can still make progress protects its group: a row that has failed for good
   * is never replayed again, so a hold or recheck flag left on it would keep the group forever.
   */
  private async cleanupStaleEntries(): Promise<void> {
    const cutoff = Date.now() - MAX_RETENTION_MS
    const protectingRows = await nativeQueueDb.requests
      .filter(
        e =>
          !e.metadata.permanentFailure &&
          (Boolean(e.metadata.licenceBlocked) || e.metadata.licenceRecheck === 'pending'),
      )
      .toArray()
    const protectedGroups = new Set(protectingRows.map(e => e.metadata.requestUUID))

    const staleEntries = await nativeQueueDb.requests
      .filter(e => e.timestamp < cutoff && !protectedGroups.has(e.metadata.requestUUID))
      .toArray()

    if (staleEntries.length === 0) return

    debug(`Cleaning up ${staleEntries.length} stale entries`)

    for (const entry of staleEntries) {
      if (entry.id !== undefined) {
        // Clean up associated blob if exists
        if (entry.metadata.fileCacheKey) {
          await nativeQueueDb.blobs.delete(entry.metadata.fileCacheKey)
        }
        await nativeQueueDb.requests.delete(entry.id)
      }
    }

    this.notifyListeners()
  }

  private async processFastQueue(entries: NativeQueueEntry[]): Promise<void> {
    if (this.fastSyncRunning || entries.length === 0) return
    this.fastSyncRunning = true

    // Drain strictly FIFO by insertion order (id), with per-requestUUID
    // head-of-line ordering: if an earlier row for a requestUUID did not fully
    // succeed (transient failure, or pending backoff), block its later
    // same-requestUUID rows for THIS pass so a newer edit cannot apply before
    // the earlier one and let a stale value win. A permanently-failed head
    // releases the lane (its later edits are the user's newer intent).
    // Different requestUUIDs drain independently. fastQueue MUST stay
    // serial-FIFO — this ordering is the correctness guarantee behind
    // latest-wins (see lib/AGENTS.md).
    const ordered = [...entries].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
    const blocked = new Set<string>()

    try {
      for (const entry of ordered) {
        const uuid = entry.metadata.requestUUID
        if (blocked.has(uuid)) {
          debug(`head-of-line: deferring ${uuid} behind an unfinished earlier edit`)
          continue
        }
        const outcome = await this.processEntry(entry)
        if (outcome === 'transient' || outcome === 'deferred' || outcome === 'licenceBlocked') {
          blocked.add(uuid)
        }
      }
    } finally {
      this.fastSyncRunning = false
    }
  }

  /**
   * Replay the media rows `replayable` lets through, oldest first. The rest are not replayed this
   * pass but still count for ordering: an earlier markup save that waits (licence hold, backoff,
   * pending create) holds back the later saves of its image.
   */
  private async processMediaQueue(
    entries: NativeQueueEntry[],
    replayable: (entry: NativeQueueEntry) => boolean,
  ): Promise<void> {
    if (this.mediaSyncRunning || entries.length === 0) return
    this.mediaSyncRunning = true

    // Markup saves of one image replay in insertion order, and an earlier one that did not go
    // through — or is not replayed this pass at all — holds back the later ones: each save draws
    // a fresh requestUUID, so without this a retried older save would land after a newer one and
    // put the old markup back on the server. A permanently-failed save is never replayed again
    // (performSync filters it out), so it never holds the lane.
    const ordered = [...entries].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
    const blockedLanes = new Set<string>()

    try {
      for (const entry of ordered) {
        const lane = markupLaneOf(entry.method, entry.url, parseBody(entry.body))
        // A photo whose sibling was refused by the licence earlier in this loop waits with it.
        if (!replayable(entry) || this.licenceBlockedThisPass.has(entry.metadata.requestUUID)) {
          if (lane) blockedLanes.add(lane)
          continue
        }
        if (lane && blockedLanes.has(lane)) {
          debug(`head-of-line: deferring a markup save behind an unfinished earlier one (${lane})`)
          continue
        }
        const outcome = await this.processEntry(entry)
        if (lane && outcome !== 'synced' && outcome !== 'permanent') blockedLanes.add(lane)
      }
    } finally {
      this.mediaSyncRunning = false
    }
  }

  private async processEntry(entry: NativeQueueEntry): Promise<ProcessOutcome> {
    if (entry.id === undefined) return 'synced'

    const meta: Metadata = { ...entry.metadata }
    if (meta.licenceBlocked && this.licenceLiftedThisPass.has(meta.requestUUID)) {
      meta.licenceBlocked = null
      meta.licenceContact = null
    }

    // Skip if entry has a future retry time (exponential backoff)
    const nextRetryAt = meta.nextRetryAt as number | undefined
    if (nextRetryAt && Date.now() < nextRetryAt) {
      debug(
        `Skipping ${meta.requestUUID}, retry scheduled for ${new Date(nextRetryAt).toISOString()}`,
      )
      return 'deferred'
    }

    // Every replay carries when the write was queued, so the server can apply the offline grace
    // window. `?? entry.timestamp` only covers a row that skipped the startup backfill.
    const enqueuedAtHeader = formatEnqueuedAt(
      typeof meta.enqueuedAt === 'number' ? meta.enqueuedAt : entry.timestamp,
    )

    try {
      let requestInit: ApiFetchInit = {
        method: entry.method,
        headers: {
          ...entry.headers,
          'X-Native-Queue-Replay': 'true',
          [QUEUE_ENQUEUED_AT_HEADER]: enqueuedAtHeader,
        },
        credentials: 'include',
      }

      if (meta.type === 'media_objects' && meta.fileCacheKey) {
        const cachedBlob = await nativeQueueDb.blobs.get(meta.fileCacheKey)

        // CRITICAL: If blob cannot be retrieved, fail the entry - don't send without file
        if (!cachedBlob?.blob) {
          debug(
            `Blob not found for ${meta.requestUUID}, fileUUID: ${meta.fileUUID}, cacheKey: ${meta.fileCacheKey}`,
          )
          // A local, deterministic failure: no licence renewal can bring the file back, so the
          // row must not stay held (it would be spared by cleanup and "remove all failed"
          // forever). Siblings keep their hold — no server verdict was received.
          meta.licenceBlocked = null
          meta.licenceContact = null
          if (meta.licenceRecheck === 'pending') meta.licenceRecheck = 'done'
          // The status and detail of an earlier server answer no longer describe this failure.
          meta.status = undefined
          meta.errorDetail = undefined
          meta.failed = true
          meta.permanentFailure = true
          meta.error = 'File data lost - please try uploading again'

          await nativeQueueDb.requests.update(entry.id, {
            metadata: meta,
            timestamp: Date.now(),
          })
          this.notifyFailure(meta)
          this.notifyListeners()
          return 'permanent'
        }

        debug(`Blob found for ${meta.requestUUID}, size: ${cachedBlob.blob.size}`)

        const formData = new FormData()
        formData.append('file', cachedBlob.blob, meta.name || 'file')

        let bodyData: Record<string, unknown> = {}
        if (entry.body) {
          try {
            bodyData = JSON.parse(entry.body)
          } catch {
            debug('Failed to parse entry body for', meta.requestUUID)
          }
        }
        Object.entries(bodyData).forEach(([key, value]) => {
          if (key !== 'file' && key !== 'fileCacheKey' && value !== undefined) {
            formData.append(key, String(value))
          }
        })
        // anno-trace: snapshot what the replay reassembled before the
        // POST goes out. If annotationData is in the keys here but the
        // server-side row doesn't store it, the bug is upstream of the
        // queue. If it's missing here but present at enqueue time, the
        // bug is in the JSON.stringify / parse round-trip.
        console.info(
          '[anno-trace] nativeQueue replay rebuilt formData ' +
            JSON.stringify({
              keys: Object.keys(bodyData),
              annotationDataType: typeof bodyData['annotationData'],
              annotationDataLength:
                typeof bodyData['annotationData'] === 'string'
                  ? (bodyData['annotationData'] as string).length
                  : null,
            }),
        )

        const newHeaders: Record<string, string> = {
          ...entry.headers,
          'X-Native-Queue-Replay': 'true',
          [QUEUE_ENQUEUED_AT_HEADER]: enqueuedAtHeader,
        }
        delete newHeaders['content-type']
        delete newHeaders['Content-Type']

        requestInit = {
          method: entry.method,
          headers: newHeaders,
          body: formData,
          credentials: 'include',
        }
      } else if (entry.body) {
        requestInit.body = entry.body
      }

      // apiFetch, not fetch: the replay is sent by THIS build, so it carries this build's
      // identity headers rather than whatever was stored at enqueue (see clientIdentity.ts).
      const response = await apiFetch(entry.url, requestInit)

      // Maintenance window: re-queue WITHOUT consuming the retry budget and
      // WITHOUT touching `timestamp` (so 7-day retention still measures from the
      // original enqueue). Return 'deferred' so the fastQueue head-of-line gate
      // stops the pass here — a later same-requestUUID edit must not apply ahead
      // of this one. The pause flag normally prevents reaching this; it's the
      // safety net for a sync already in flight when the window opens.
      if (response.status === 503 && response.headers.get('x-maintenance-mode') === 'true') {
        meta.nextRetryAt = Date.now() + 60_000
        await nativeQueueDb.requests.update(entry.id, { metadata: meta })
        this.notifyListeners()
        debug(`Maintenance 503 for ${meta.requestUUID} — deferred without counting retry`)
        return 'deferred'
      }

      // Licence refusal: recognised by the header, not the status, as with maintenance. Holds
      // the whole group without consuming a retry; see markLicenceBlocked.
      const licenceReason =
        response.status === 403 ? response.headers.get(LICENCE_BLOCK_HEADER) : null
      if (licenceReason) {
        await this.markLicenceBlocked(entry, meta, licenceReason, response)
        return 'licenceBlocked'
      }

      if (!response.ok) {
        const isClientErr = response.status >= 400 && response.status < 500

        // A held row answered 5xx: the server could not say whether the licence still blocks it.
        // Keep the hold and spend no retry — held rows replay only on licence triggers (hourly at
        // most), so this cannot loop, and counting it would turn held work into a permanent
        // failure that replay then skips for good.
        if (meta.licenceBlocked && !isClientErr) {
          meta.error = `HTTP ${response.status}: ${response.statusText}`
          await nativeQueueDb.requests.update(entry.id, { metadata: meta })
          this.notifyListeners()
          return 'licenceBlocked'
        }
        if (meta.licenceBlocked) {
          meta.licenceBlocked = null
          meta.licenceContact = null
          await this.liftLicenceHold(meta.requestUUID)
        }

        // A 403 recorded by this (C8) client is already a deliberate verdict; only rows recorded
        // by a pre-C8 client get the one startup recheck. And that recheck is spent once the
        // server has answered it.
        if (meta.licenceRecheck === 'pending' || response.status === 403) {
          meta.licenceRecheck = 'done'
        }
        meta.retries = (meta.retries ?? 0) + 1
        meta.failed = true
        meta.error = `HTTP ${response.status}: ${response.statusText}`
        // Surface the HTTP status as a structured field so failure
        // listeners can map it to friendlier UX (403 → permission denied,
        // 5xx → server error) without parsing the human string.
        meta.status = response.status
        // Pull a server-provided detail out of the response body when
        // available — JSON-LD/Hydra exposes it under `hydra:description`
        // or `detail`. Cloned because we already consumed the response
        // status above and may read the body again here. Best-effort:
        // failures to parse silently fall back to the generic message.
        try {
          const cloned = response.clone()
          const ct = cloned.headers.get('content-type') ?? ''
          if (ct.includes('json')) {
            const body = (await cloned.json()) as Record<string, unknown> | null
            const detail =
              (body?.['hydra:description'] as string | undefined) ??
              (body?.['detail'] as string | undefined) ??
              (body?.['description'] as string | undefined) ??
              (body?.['title'] as string | undefined)
            if (typeof detail === 'string' && detail.length > 0) {
              meta.errorDetail = detail
            }
          }
        } catch {
          // body unreadable — leave errorDetail unset
        }

        if (isClientErr || meta.retries >= MAX_RETRIES) {
          meta.permanentFailure = true
          this.notifyFailure(meta)
          // Keep blobs on permanent failure so user can retry after resetting.
          // Blobs are cleaned up in deleteEntry(), deleteAllFailed(), and cleanupStaleEntries().
        } else {
          // Calculate exponential backoff for next retry
          const backoffMs = Math.min(
            BACKOFF_BASE_MS * Math.pow(2, meta.retries - 1),
            BACKOFF_MAX_MS,
          )
          meta.nextRetryAt = Date.now() + backoffMs
          debug(`Retry ${meta.retries} for ${meta.requestUUID}, next attempt in ${backoffMs}ms`)
        }

        await nativeQueueDb.requests.update(entry.id, {
          metadata: meta,
          timestamp: Date.now(),
        })
        this.notifyListeners()
        return meta.permanentFailure ? 'permanent' : 'transient'
      }

      // Parse JSON response bodies before deleting the entry so success
      // listeners can read endpoint-specific payloads (e.g. per-id
      // outcomes from /issues/bulk). Cheap on the typical small-issue
      // response; silently skipped for non-JSON or parse errors. The replay
      // bypasses the axios interceptor, so the signed media URLs are adopted
      // here, before any success listener reads the body (AUTHZ-02 E2).
      let responseBody: unknown = undefined
      const contentType = response.headers.get('content-type') ?? ''
      if (contentType.includes('json')) {
        try {
          responseBody = adoptSignedMediaUrls(await response.json())
        } catch {
          // body unreadable or empty — leave responseBody undefined
        }
      }

      if (meta.fileCacheKey) {
        await nativeQueueDb.blobs.delete(meta.fileCacheKey)
      }
      await nativeQueueDb.requests.delete(entry.id)
      if (meta.licenceBlocked) await this.liftLicenceHold(meta.requestUUID)
      debug(`Successfully synced ${meta.requestUUID}`)
      this.notifySuccess(meta, responseBody)
      this.notifyListeners()
      return 'synced'
    } catch (err) {
      // Same as a 5xx on a held row: no answer about the licence, so keep the hold, spend nothing.
      if (meta.licenceBlocked) {
        meta.error = err instanceof Error ? err.message : 'Unknown error'
        await nativeQueueDb.requests.update(entry.id, { metadata: meta })
        this.notifyListeners()
        return 'licenceBlocked'
      }
      meta.failed = true
      meta.retries = (meta.retries ?? 0) + 1
      meta.error = err instanceof Error ? err.message : 'Unknown error'

      if (meta.retries >= MAX_RETRIES) {
        meta.permanentFailure = true
        // Out of attempts without the server ever answering: the recheck is spent too, or the
        // row would stay protected from cleanup forever.
        if (meta.licenceRecheck === 'pending') meta.licenceRecheck = 'done'
        this.notifyFailure(meta)
        // Keep blobs on permanent failure so user can retry after resetting.
        // Blobs are cleaned up in deleteEntry(), deleteAllFailed(), and cleanupStaleEntries().
      } else {
        // Calculate exponential backoff for next retry
        const backoffMs = Math.min(BACKOFF_BASE_MS * Math.pow(2, meta.retries - 1), BACKOFF_MAX_MS)
        meta.nextRetryAt = Date.now() + backoffMs
        debug(`Retry ${meta.retries} for ${meta.requestUUID}, next attempt in ${backoffMs}ms`)
      }

      await nativeQueueDb.requests.update(entry.id, {
        metadata: meta,
        timestamp: Date.now(),
      })
      this.notifyListeners()
      return meta.permanentFailure ? 'permanent' : 'transient'
    }
  }
}

export const nativeQueueManager = new NativeQueueManager()
